import { DecimalSchema } from "../../decimal/decimal.schema";
import z from "zod";

export const VolumeByDaySchema = z.object({
  bucket: z.string(),
  campaignName: z.string(),
  exchangeId: z.string().nullable(),
  vol: DecimalSchema,
});
export type VolumeByDay = z.infer<typeof VolumeByDaySchema>;

export const VolumeByEpochSchema = z.object({
  epochId: z.number(),
  campaignName: z.string(),
  exchangeId: z.string().nullable(),
  vol: DecimalSchema,
});
export type VolumeByEpoch = z.infer<typeof VolumeByEpochSchema>;

export const SkywalkerStatsSchema = z.object({
  volume: z.array(VolumeByDaySchema),
  volumeByEpoch: z.array(VolumeByEpochSchema),
  traders: z.number(),
  totalVolume: DecimalSchema,
  avg: DecimalSchema,
  ratio: DecimalSchema,
  medianVolume: DecimalSchema,
  medianOfAllTrades: DecimalSchema,
  totalRewards: DecimalSchema,
});
export type SkywalkerStats = z.infer<typeof SkywalkerStatsSchema>;

// Hourly volume breakdown for a specific epoch
export const HourlyVolumeEntrySchema = z.object({
  hour: z.string(), // ISO timestamp for the hour bucket
  exchangeId: z.string().nullable(),
  vol: DecimalSchema,
});
export type HourlyVolumeEntry = z.infer<typeof HourlyVolumeEntrySchema>;

export const EpochHourlyVolumeSchema = z.object({
  epochId: z.number(),
  campaignName: z.string(),
  hourlyVolume: z.array(HourlyVolumeEntrySchema),
});
export type EpochHourlyVolume = z.infer<typeof EpochHourlyVolumeSchema>;

export const EpochHourlyVolumeResponseSchema = z.array(EpochHourlyVolumeSchema);
export type EpochHourlyVolumeResponse = z.infer<typeof EpochHourlyVolumeResponseSchema>;

// Trader counts + volume per trader, total and per epoch.
// totals.traders is a distinct-user count: it is NOT the sum of byEpoch traders
// (a user active in several epochs counts once in totals).
export const EpochTraderStatsEntrySchema = z.object({
  traders: z.number(),
  volumeUSD: DecimalSchema,
  tradeCount: z.number(),
  avgVolumePerTrader: DecimalSchema,
});
export type EpochTraderStatsEntry = z.infer<typeof EpochTraderStatsEntrySchema>;

export const SkywalkerEpochTraderStatsSchema = z.object({
  totals: EpochTraderStatsEntrySchema,
  byEpoch: z.array(EpochTraderStatsEntrySchema.extend({ epochId: z.number() })),
});
export type SkywalkerEpochTraderStats = z.infer<typeof SkywalkerEpochTraderStatsSchema>;

export const SkywalkerCampaignUserSchema = z.object({
  hubUserId: z.number(),
  defiAddress: z.string().nullable(),
  volumeUSD: DecimalSchema,
  tradeCount: z.number(),
});
export type SkywalkerCampaignUser = z.infer<typeof SkywalkerCampaignUserSchema>;

export const SkywalkerCampaignUsersResponseSchema = z.array(SkywalkerCampaignUserSchema);
export type SkywalkerCampaignUsersResponse = z.infer<typeof SkywalkerCampaignUsersResponseSchema>;

export const SkywalkerUserVolumeDetailSchema = z.object({
  hubUserId: z.number(),
  defiAddress: z.string().nullable(),
  total: z.object({ volumeUSD: DecimalSchema, tradeCount: z.number() }),
  byEpoch: z.array(z.object({ epochId: z.number(), volumeUSD: DecimalSchema, tradeCount: z.number() })),
});
export type SkywalkerUserVolumeDetail = z.infer<typeof SkywalkerUserVolumeDetailSchema>;

// Users per country. A user's country is the countryCode with the most trades
// (argmax): per-epoch entries argmax within the epoch, total argmaxes across
// epochs — so byEpoch counts do NOT sum to total. countryCode null = unknown.
// volumeUSD sums each user's TOTAL volume into their dominant country (same
// per-user attribution as `users`), so users% and volume% share one denominator.
export const CountryUsersSchema = z.object({
  countryCode: z.string().nullable(),
  users: z.number(),
  volumeUSD: DecimalSchema,
});
export type CountryUsers = z.infer<typeof CountryUsersSchema>;

export const SkywalkerUserGeographySchema = z.object({
  total: z.array(CountryUsersSchema),
  byEpoch: z.array(z.object({ epochId: z.number(), countries: z.array(CountryUsersSchema) })),
});
export type SkywalkerUserGeography = z.infer<typeof SkywalkerUserGeographySchema>;

// Cross-campaign geography: `global` argmaxes each user's country across ALL
// campaigns (each user counted once). `campaigns` lists every campaign with
// geography data, ordered newest-first — the per-campaign breakdown is fetched
// lazily via the single-campaign endpoint when a row is expanded.
export const SkywalkerGlobalUserGeographySchema = z.object({
  global: z.array(CountryUsersSchema),
  campaigns: z.array(z.string()),
});
export type SkywalkerGlobalUserGeography = z.infer<typeof SkywalkerGlobalUserGeographySchema>;
